import { UpdatedBadge } from "app/pages/sales-intelligence/common-components/badge/UpdatedBadge";
import { filterUsageMap } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterDefinition";
import { showsLegacyIndustry } from "app/pages/sales-intelligence/modules/industry/utils/industryVisibility";
import settingsService from "app/pages/sales-intelligence/sub-modules/advanced-search/services/sw-settings/settingsService";

import LegacyIndustryFilter from "../../components/filters/LegacyIndustryFilter/LegacyIndustryFilter";
import LegacyIndustryFilterResult from "../../components/filters/LegacyIndustryFilter/LegacyIndustryFilterResult";
import LegacyIndustryFilterWithDemoCallout from "../../components/filters/LegacyIndustryFilter/LegacyIndustryFilterWithDemoCallout";
import { leadsFilterApiCategory } from "../../types/leadsFilterApiCategory";
import { leadsFilterCategoryMap } from "../../types/leadsFilterCategory";
import leadsSearchFiltersI18nKeys from "../../utils/i18n";
import { defineLeadsFilter } from "../defineLeadsFilter";

import LegacyIndustryFilterModel from "./LegacyIndustryFilterModel";

/**
 * @deprecated Registers the legacy industry filter. Superseded by `industryTaxonomyDefinition`
 *
 * No `requires`, and deliberately not the description: the model tolerates an absent description (its options
 * fall back to empties), so the card fills in as the description lands. The opt-in that decides whether this
 * card exists at all is not a build input either — the SI root resolves it before any page renders
 * (`onEnterSalesIntelligenceRoot`), so `render` reads a settled value and this card is buildable immediately.
 *
 * A URL-seeded / saved value reaches the model through redux by the page-root init (see
 * `useApplyInitialFilters`), independent of when either fetch resolves.
 */
export const industryFilterDefinition = defineLeadsFilter({
    key: "industryFilter",
    apiCategory: leadsFilterApiCategory.industryFilter,
    category: leadsFilterCategoryMap.company,
    trackingName: "industry",
    titleKey: { key: leadsSearchFiltersI18nKeys.industryFilterCategoriesTitle },

    render: (ctx) => {
        // Hidden whenever the taxonomy takes over — permanently for an opted-in account, or for the duration
        // of the industry demo. One predicate, so the two industry cards cannot both show or both hide.
        if (!showsLegacyIndustry(ctx)) {
            return undefined;
        }

        // The "UPDATED" badge + "Try in demo mode" callout are offered only where the demo can run: search
        // surfaces (advanced search / lists). An opted-in account never reaches here, so the demo — and with
        // it the `demoMode` marker on search requests — is unreachable for them by construction.
        const canOfferDemo = ctx.usage === filterUsageMap.search;

        return {
            tag: canOfferDemo ? UpdatedBadge : undefined,
            tooltipText: { key: "si.lead_gen_filters.group.categories.tooltip" },
            filter: new LegacyIndustryFilterModel(
                {
                    options: {
                        industry: ctx.description?.industry ?? { industries: {}, inclusionOptions: [] },
                        industrySuggestions: ctx.description?.industrySuggestions ?? {},
                    },
                },
                settingsService,
            ),
            filterView: canOfferDemo ? LegacyIndustryFilterWithDemoCallout : LegacyIndustryFilter,
            filterResultView: LegacyIndustryFilterResult,
        };
    },
});
